import type { DestinationIconName } from "~/components/layout/nav-icon";
import { foldQuery, parseDiscoveryQuery } from "./discovery-query";
import { matchNcmChapters, ncmChapterCode } from "./ncm-chapter";

const OPEN_ENDED_FISCAL_TERMS =
  /\b(?:fiscal\w*|tribut\w*|impost\w*|tax\w*|aliquot\w*|regra\w*|norma\w*|legisla\w*|lei\w*|decreto\w*|convenio\w*|benefic\w*|mercador\w*|produto\w*|ncm|icms|ipi|cest|mva|iss|pis|cofins|difal|sefaz|receita|confaz|classif\w*|siscomex)\b/;

export type OperatorHitKind = "launch" | "catalog" | "destination" | "record";
export type CatalogScope = "ncm" | "prefix" | "chapter";

export type OperatorTarget = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string | undefined>;
};

export type OperatorHit = OperatorTarget & {
  id: string;
  kind: OperatorHitKind;
  group: string;
  label: string;
  hint?: string;
  catalogScope?: CatalogScope;
  chapter?: string;
  icon?: DestinationIconName;
};

export type OperatorRecord = OperatorTarget & {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon?: DestinationIconName;
};

export function filterDestinations(
  destinations: Array<{ group: string; label: string; to: string; icon?: DestinationIconName }>,
  query: string,
): OperatorHit[] {
  const needle = query.trim().toLocaleLowerCase();
  return destinations
    .filter((destination) => {
      if (!needle) return true;
      return `${destination.label} ${destination.group} ${destination.to}`
        .toLocaleLowerCase()
        .includes(needle);
    })
    .map((destination) => ({
      id: `destination:${destination.to}`,
      kind: "destination" as const,
      group: destination.group,
      label: destination.label,
      hint: destination.to,
      to: destination.to,
      icon: destination.icon,
    }));
}

export function launchHitForQuery(
  query: string,
  copy: { group: string; label: string; hint: string },
): OperatorHit | null {
  const intent = parseDiscoveryQuery(query);
  if (intent.kind === "empty") return null;
  const hasStructuredFiscalIntent =
    intent.family !== null || intent.ncm !== null || intent.ufs.length > 0;
  const isNaturalFiscalQuestion = OPEN_ENDED_FISCAL_TERMS.test(foldQuery(intent.query));
  if (!hasStructuredFiscalIntent && !isNaturalFiscalQuestion) return null;
  return {
    id: `launch:${intent.query}`,
    kind: "launch",
    group: copy.group,
    label: copy.label,
    hint: copy.hint,
    to: "/evidence",
    search: { tab: "discovery", desk: intent.query },
  };
}

function catalogTarget(ncm: string): Pick<OperatorHit, "to" | "search"> {
  return {
    to: "/catalog/ncm",
    search: { ncm, q: ncm },
  };
}

function chapterTarget(chapter: string): Pick<OperatorHit, "to" | "params"> {
  return {
    to: "/rules/chapters/$chapter",
    params: { chapter },
  };
}

/** Official 8-digit Classif lookup. This is catalog provenance, not a rate. */
export function catalogHitForQuery(query: string): OperatorHit | null {
  const intent = parseDiscoveryQuery(query);
  if (intent.kind === "empty" || !intent.ncm) return null;
  return {
    id: `catalog:${intent.ncm}`,
    kind: "catalog",
    group: "catalog",
    label: intent.ncm,
    catalogScope: "ncm",
    chapter: ncmChapterCode(intent.ncm) ?? undefined,
    ...catalogTarget(intent.ncm),
  };
}

/**
 * Brazilian tax catalog lookup for command search.
 * 8-digit NCM, numeric prefixes, or official SH chapter titles.
 * Never a published aliquota.
 */
export function catalogHitsForQuery(query: string, locale = "pt-BR"): OperatorHit[] {
  const exact = catalogHitForQuery(query);
  if (exact) return [exact];

  const digits = query.replace(/\D/g, "");
  if (digits.length >= 4 && digits.length <= 8) {
    return [
      {
        id: `catalog:prefix:${digits}`,
        kind: "catalog",
        group: "catalog",
        label: digits,
        catalogScope: "prefix",
        chapter: ncmChapterCode(digits) ?? undefined,
        ...catalogTarget(digits),
      },
    ];
  }

  return matchNcmChapters(query, locale).map((match) => ({
    id: `catalog:chapter:${match.chapter}`,
    kind: "catalog" as const,
    group: "catalog",
    label: match.chapter,
    hint: match.title,
    catalogScope: "chapter" as const,
    chapter: match.chapter,
    ...chapterTarget(match.chapter),
  }));
}

export function mergeOperatorHits(input: {
  launch: OperatorHit | null;
  catalog?: readonly OperatorHit[] | OperatorHit | null;
  destinations: OperatorHit[];
  records: OperatorRecord[];
}): OperatorHit[] {
  const records = input.records.map((record) => ({
    ...record,
    kind: "record" as const,
  }));
  const catalog = (
    Array.isArray(input.catalog) ? input.catalog : input.catalog ? [input.catalog] : []
  )
    .slice()
    .sort((left, right) => catalogHitRank(left) - catalogHitRank(right));
  return [...(input.launch ? [input.launch] : []), ...catalog, ...input.destinations, ...records];
}

/** Exact/prefix codes, then product NCM hits, then chapter-title hits. */
function catalogHitRank(hit: OperatorHit): number {
  if (hit.catalogScope === "ncm" || hit.catalogScope === "prefix") return 0;
  if (hit.catalogScope === "chapter") return 2;
  return 1;
}
